import { z } from "zod";

type PhoneValidatorOptions = {
  minLength: number;
  maxLength: number;
  startWith: string[]; // local-only start rules (supports wildcard '*')
  simpleError?: boolean; // simplify error to length-only message
  msgType: "phone" | "digits";
};

type AccountNoValidatorOptions = Omit<PhoneValidatorOptions, "msgType">;

type ValidatorSchemaType = {
  username: z.ZodString;
  phone: (options: PhoneValidatorOptions) => z.ZodString;
  otp: z.ZodString;
  email: z.ZodString;
  legalName: z.ZodString;
  firstName: z.ZodString;
  accountName: z.ZodString;
  lastName: z.ZodString;
  payment: {
    eWallet: {
      accountNo: (options: AccountNoValidatorOptions) => z.ZodString;
      accountType: z.ZodString;
      accountKey: z.ZodString;
      isDefault: z.ZodOptional<z.ZodBoolean>;
    };
    bank: {
      bankKey: z.ZodString;
      accountNo: z.ZodString;
      accountNoDefault: z.ZodString;
      accountNoINR: z.ZodString;
      ifsc: z.ZodString;
    };
  };
  loginPassword: z.ZodString;
  loginPin: z.ZodString;
  registerPassword: z.ZodString;
  pan: z.ZodString;
  upiId: z.ZodString;
  cpf: z.ZodString;
  curp: z.ZodString;
  clabeNumber: z.ZodString;
  pixKeyType: z.ZodEnum<["cpf", "email", "phone", "evp"]>;
  pixKeyCpf: z.ZodString;
  pixKeyEmail: z.ZodString;
  pixKeyPhone: (options: PhoneValidatorOptions) => z.ZodString;
  pixKeyRandom: z.ZodString;
};

export const ValidatorSchema: ValidatorSchemaType = {
  username: z
    .string()
    .min(4, "4-13 alphanumeric without special character.")
    .max(13, "4-13 alphanumeric without special character.")
    .regex(/^\w+$/, "only letters, numbers, and underscores (_) are allowed."),
  loginPassword: z.string().min(8, "at least 8 characters with letters & numbers"),
  loginPin: z.string().min(4, "PIN length is invalid"),
  registerPassword: z
    .string()
    .min(8, "at least 8 characters with letters & numbers")
    .regex(/^(?=.*[A-Za-z])(?=.*\d).+$/, "at least 8 characters with letters & numbers")
    .regex(/^\S+$/, "spaces are not allowed"),
  phone: (options: PhoneValidatorOptions) => {
    const { minLength, maxLength, startWith, simpleError, msgType } = options;
    let errMsgStartWith = "";
    let errMsgDigitsOnly = "";
    const isSingleLength = minLength === maxLength;
    switch (msgType) {
      case "phone":
        if (isSingleLength) {
          // Display single length message, no need to display startWith
          errMsgDigitsOnly = "phone number must be {{minLength}} digits";
        } else {
          // Display multiple length message, no need to display startWith
          errMsgDigitsOnly = "phone number must be {{minLength}}-{{maxLength}} digits";
        }

        errMsgStartWith = simpleError ? errMsgDigitsOnly : "phone number should start with {{startWith}}";
        break;
      case "digits":
        if (isSingleLength) {
          // Display single length message, need to display startWith
          errMsgStartWith = simpleError
            ? "enter a {{minLength}}-digit number"
            : "enter a {{minLength}}-digit number starting with {{startWith}}";
          errMsgDigitsOnly = errMsgStartWith;
        } else {
          // Display multiple length message, no need to display startWith
          errMsgStartWith = simpleError
            ? "enter a {{minLength}}-{{maxLength}} digit number"
            : "enter a {{minLength}}-{{maxLength}} digit number starting with {{startWith}}";
          errMsgDigitsOnly = errMsgStartWith;
        }
        break;
    }

    const startWithRegex = buildStartWithRegex(startWith);
    const digitRegex = buildLocalDigitLengthRegexes(minLength, maxLength, startWith);

    return z.string().regex(startWithRegex, errMsgStartWith).regex(digitRegex, errMsgDigitsOnly);
  },
  otp: z.string().min(1, "otp is required. click on get code"),
  legalName: z.string().min(1, "required"),
  firstName: z
    .string({
      required_error: "required",
    })
    .min(1, "required"),
  accountName: z
    .string({
      required_error: "required",
    })
    .min(1, "required"),
  lastName: z
    .string({
      required_error: "required",
    })
    .min(1, "required"),
  payment: {
    eWallet: {
      accountNo: (options: AccountNoValidatorOptions) => {
        const { minLength, maxLength, startWith, simpleError } = options;
        const startWithRegex = buildStartWithRegex(startWith);
        const accountNoRegex = buildLocalDigitLengthRegexes(minLength, maxLength, startWith);
        const isSingleLength = minLength === maxLength;
        let errMsg;
        if (isSingleLength) {
          errMsg = simpleError
            ? "enter a {{minLength}}-digit number"
            : "enter a {{minLength}}-digit number starting with {{startWith}}";
        } else {
          errMsg = simpleError
            ? "enter a {{minLength}}-{{maxLength}} digit number"
            : "enter a {{minLength}}-{{maxLength}} digit number starting with {{startWith}}";
        }
        return z
          .string({
            required_error: "required",
          })
          .min(1, "required")
          .regex(startWithRegex, errMsg)
          .regex(accountNoRegex, errMsg);
      },
      accountType: z
        .string({
          required_error: "required",
        })
        .min(1, "required"),
      accountKey: z
        .string({
          required_error: "required",
        })
        .min(1, "required"),
      isDefault: z.boolean().optional(),
    },
    bank: {
      bankKey: z
        .string({
          required_error: "required",
        })
        .min(1, "required"),
      accountNo: z
        .string({
          required_error: "required",
        })
        .min(1, "required")
        .regex(/^\d+$/, "account number must be digits only")
        .regex(/^\d{5,34}$/, "account number must be 5-34 digits"),
      accountNoDefault: z
        .string({
          required_error: "required",
        })
        .min(1, "required")
        .regex(/^\d+$/, "account number must be digits only")
        .regex(/^\d{5,34}$/, "account number must be 5-34 digits"),
      accountNoINR: z
        .string({
          required_error: "required",
        })
        .min(1, "required")
        .regex(/^\d+$/, "account number must be digits only")
        .regex(/^\d{6,19}$/, "account number must be 6-19 digits"),
      ifsc: z
        .string({
          required_error: "required",
        })
        .min(1, "required")
        .length(11, "ifsc code must be 11 characters")
        .regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, "ifsc code is not valid"),
    },
  },
  pan: z
    .string({ required_error: "required" })
    .min(1, "required")
    .length(10, "pan number must be 10 characters")
    .regex(/^[A-Z]{5}[0-9]{4}[A-Z]$/, "pan number is not valid"),
  upiId: z
    .string({
      required_error: "required",
    })
    .min(1, "required")
    .regex(/^[A-Za-z0-9._-]{3,}@[A-Za-z0-9.-]{2,}$/, "upi id is not valid"),
  cpf: z
    .string({
      required_error: "required",
    })
    .min(1, "required")
    .regex(/^\d+$/, "cpf number must be digits only")
    .regex(/^\d{11}$/, "cpf number must be 11 digits"),
  curp: z
    .string({
      required_error: "required",
    })
    .min(1, "required")
    .length(18, "curp number must be 18 characters")
    .regex(/^[A-Z]{4}\d{6}[HM][A-Z]{2}[A-Z]{3}[A-Z0-9]{2}$/, "curp number is not valid"),
  clabeNumber: z
    .string({
      required_error: "required",
    })
    .min(1, "required")
    .regex(/^\d+$/, "clabe must be digits only")
    .regex(/^\d{18}$/, "clabe must be 18 digits"),
  pixKeyType: z.enum(["cpf", "email", "phone", "evp"]),
  pixKeyCpf: z
    .string({
      required_error: "required",
    })
    .min(1, "required")
    .regex(/^\d+$/, "cpf number must be digits only")
    .regex(/^\d{11}$/, "cpf number must be 11 digits"),
  pixKeyEmail: z
    .string({
      required_error: "required",
    })
    .min(1, "required")
    .email("email is not valid"),
  pixKeyPhone: (options: PhoneValidatorOptions) => ValidatorSchema.phone(options),
  pixKeyRandom: z
    .string({
      required_error: "required",
    })
    .min(1, "required")
    .length(36, "random key must be 36 characters")
    .regex(/^[A-Fa-f0-9]{8}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{12}$/),
  email: z
    .string({
      required_error: "required",
    })
    .min(1, "required")
    .email("email is not valid"),
};

// Build local full-match regex from an array of masks and target lengths
const buildLocalDigitLengthRegexes = (minLength: number, maxLength: number, startWithMasks: string[]): RegExp => {
  const lengths: number[] = [];
  for (let i = minLength; i <= maxLength; i++) lengths.push(i);
  const regs: string[] = [];

  // if no startWith, return length regex only
  if (startWithMasks.length === 0) {
    lengths.forEach((length) => {
      regs.push(`^\\d{${length}}$`);
    });
  } else {
    // if startWith, return startWith regex and length regex
    startWithMasks.forEach((mask) => {
      const maskRegex = maskToRegex(mask);
      const maskDigits = countMaskDigits(mask);
      lengths.forEach((length) => {
        const bodyLen = length - maskDigits;
        if (bodyLen >= 0) regs.push(`^${maskRegex}\\d{${bodyLen}}$`);
      });
    });
  }

  return new RegExp(regs.join("|"));
};

// Build a start-with regex for local masks only
const buildStartWithRegex = (startWithMasks: string[]): RegExp => {
  const localPart = startWithMasks.map((m) => maskToRegex(m)).join("|");
  return new RegExp(`^(?:${localPart})`);
};

const maskToRegex = (mask: string): string => {
  // Escape regex special characters first, then replace escaped '*' back to \\d
  const escaped = escapeRegex(mask);
  return escaped.replace(/\\\*/g, "\\d");
};

const countMaskDigits = (mask: string): number => {
  // '*' counts as one digit; other characters count as themselves
  return mask.replace(/\*/g, "0").length;
};

const escapeRegex = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
